export const trendsEn = {
  title: "Trends",
  subtitle: "Pain patterns, events, and analytical insights",
  export: "Export",
  exportMovedCta: "Doctor Visits",
  logEvent: "Log Event",
  chart: {
    title: "Pain Intensity Trend",
    info: {
      what: "Daily average pain intensity over time, with optional event overlays and temperature context.",
      how: "Each point is your daily mean pain score (0-10). The dashed reference line is the mean for the selected period. Event spans mark dates where logged events overlap.",
      why: "This helps you quickly see trend direction, flare periods, and whether events align with changes in pain.",
    },
    loading: "Loading chart data...",
    noData: "No pain data recorded for this period.",
    noDataHint: "Start tracking to see your trends over time.",
    noDataTooltip: "No data recorded",
    eventsInPeriod: "Events in this period: {{count}}",
    moreEvents: "+{{count}} more",
    showMoreEvents: "Show {{count}} more",
    showLessEvents: "Show less",
    averagePain: "Average Pain",
    meanLine: "Period mean",
    temperatureToggle: "Temperature",
    temperatureLabel: "Temperature",
    temperatureSetLocation: "Set location in Settings",
    trendDateRange: "Trend date range",
    ariaLabel: "Pain intensity trend chart for the past {{range}}",
  },
  info: {
    aria: "Learn more",
    whatLabel: "What this shows",
    howLabel: "How it's calculated",
    whyLabel: "Why it matters",
  },
  dateRange: {
    "7d": "7 Days",
    "30d": "30 Days",
    "90d": "90 Days",
    "180d": "6 Months",
    "365d": "1 Year",
    all: "All Time",
  },
  sections: {
    overview: "Overview",
    insights: "Insights",
    events: "Events",
    body: "Body",
  },
  insights: {
    title: "Insights",
    info: {
      what: "AI-generated observations and patterns from your selected date range.",
      how: "Insights are generated from your tracked records and ranked by priority. They include trend, pattern, correlation, progress, and focus suggestions.",
      why: "They help you spot meaningful changes faster and guide what to discuss with your care team or review in your routine.",
    },
    show: "Show",
    hide: "Hide",
    expandAriaLabel: "Expand insights",
    collapseAriaLabel: "Collapse insights",
    noInsights: "No insights available for this time period.",
    noInsightsHint: "Continue logging to see patterns in your data.",
    error: "Unable to load insights. Please try again later.",
    type: {
      trend_observation: "Trend",
      pattern_identification: "Pattern",
      correlation_observation: "Observation",
      progress_indicator: "Progress",
      focus_suggestion: "Suggestion",
    },
  },
  error: {
    title: "Unable to load trends",
    description: "Something went wrong while loading your pain trends.",
    retry: "Try Again",
  },
  empty: {
    title: "No trend data yet",
    description:
      "Start tracking your daily pain to see patterns and trends over time. Your data will help identify triggers and track progress.",
    startTracking: "Start Tracking",
  },
  stats: {
    sectionTitle: "Key Metrics",
    info: {
      what: "Core tracking coverage and pain intensity summary for the selected period.",
      how: "Days tracked counts days with entries. Consistency/tracking is tracked days divided by total days in range. Average pain is mean daily pain; peak pain is the highest daily value.",
      why: "These metrics tell you data quality and baseline symptom load, helping you interpret the other analytics with the right context.",
    },
    daysTracked: "Days Tracked",
    consistency: "{{value}}% consistency",
    averagePain: "Average Pain",
    dailyAverage: "Daily average intensity",
    peakPain: "Peak Pain",
    highestRecorded: "Highest recorded",
    tracking: "Tracking",
    daysWithRecords: "Days with records",
  },
  locations: {
    title: "Pain Locations",
    topTitle: "Top Pain Locations",
    noLocations: "No pain locations recorded in this period.",
    daysAvg: "{{days}} days \u2022 avg {{avg}}/10",
    mostFrequent: "Most frequent location ({{percentage}}% of tracked days)",
    moreLocations: "+{{count}} more locations tracked",
  },
  burden: {
    title: "Body Region Burden",
    info: {
      what: "A ranked body-map style burden score per region.",
      how: "Each region combines frequency (35%), average intensity (45%), and tracking coverage (20%) into a 0-100 burden score, then sorts highest to lowest.",
      why: "It helps prioritize which body areas are most persistent and impactful, so reviews and interventions can focus where they matter most.",
    },
    description:
      "Composite score combining pain frequency, intensity, and tracking coverage. Higher burden = more persistent, intense pain in that region.",
    tier: {
      critical: "Critical",
      high: "High",
      moderate: "Moderate",
      low: "Low",
    },
    segFrequency: "Frequency: {{value}}",
    segIntensity: "Intensity: {{value}}",
    segCoverage: "Coverage: {{value}}",
    legendFrequency: "Frequency (35%)",
    legendIntensity: "Intensity (45%)",
    legendCoverage: "Coverage (20%)",
    footnote:
      "Burden score (0\u2013100) = frequency weight (35%) + intensity weight (45%) + coverage weight (20%). This is an observational metric, not a clinical diagnosis.",
  },
  // --- Stability & Volatility ---
  stability: {
    title: "Stability & Volatility",
    info: {
      what: "How steady or variable your pain is across this period.",
      how: "Volatility uses standard deviation of daily pain. Daily swing is mean day-to-day absolute change. Direction compares first and last trend levels. Best run counts consecutive days without increase.",
      why: "Stable patterns are easier to manage and predict. Rising volatility or direction can signal a need to review triggers, pacing, or treatment changes.",
    },
    volatility: "Volatility",
    low: "Low",
    moderate: "Moderate",
    high: "High",
    stdDev: "SD",
    dailySwing: "Daily swing",
    avgChange: "avg change",
    direction: "Direction",
    trending_up: "Trending up",
    trending_down: "Trending down",
    stable: "Stable",
    ptsShift: "pts shift",
    stableRun: "Best run",
    days: "days",
    noIncrease: "without increase",
    pts: "pts",
  },
  // --- Flare Forecast ---
  forecast: {
    title: "Flare Forecast",
    info: {
      what: "A 7-day relative risk outlook for pain flare likelihood.",
      how: "Forecast scores are derived from recent pain patterns, day-of-week behavior, event overlap, and optionally weather adjustment when enabled.",
      why: "It supports short-term planning by highlighting days that may need lighter pacing or stronger symptom management.",
    },
    description: "7-day risk projection based on your recent patterns",
    basedOn: "Based on last {{days}} days",
    confidenceHigh: "High confidence",
    confidenceModerate: "Moderate confidence",
    confidenceLow: "Low confidence",
    dataPoints: "data points",
    today: "Today",
    risk: {
      low: "Low",
      moderate: "Moderate",
      high: "High",
    },
    footnote:
      "This projection is based on your historical pain patterns, event overlaps, and day-of-week trends. It is observational and not a medical diagnosis or prediction. Consult your healthcare provider for clinical advice.",
    weatherAdjusted: "Weather patterns factored in",
    weatherToggle: "Weather adjustment",
    weatherInfo:
      "Forecast scores are nudged based on your observed pain-temperature pattern and the upcoming weather forecast. This is observational, not a clinical prediction.",
    days: {
      sun: "Sun",
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",
    },
  },
  // --- Event Impact ---
  eventImpact: {
    title: "Event Impact",
    info: {
      what: "How pain changes before, during, and after each logged event.",
      how: "For each event, it compares average pain in three windows: 7 days before, event days, and 7 days after. Change is during minus before.",
      why: "This highlights events that may coincide with improvement or worsening, helping you evaluate practical impact over time.",
    },
    description: "Average pain 7 days before, during, and after each event",
    event: "Event",
    before: "Before",
    during: "During",
    after: "After",
    change: "Change",
    sortBy: "Sort by",
    sortDirection: "Order",
    sort: {
      default: "Default",
      name: "Event name",
      before: "Before",
      during: "During",
      after: "After",
      change: "Change",
      asc: "Ascending",
      desc: "Descending",
    },
  },
  // --- Category Impact ---
  categoryImpact: {
    title: "Category Impact",
    info: {
      what: "Category-level comparison of pain during events versus outside events.",
      how: "Events are grouped by category, then average pain is computed for days during category events and days outside those events. Delta = during minus outside.",
      why: "This helps reveal broader patterns (for example medication or health-status related changes) beyond single events.",
    },
    description:
      "Average pain during events vs. outside events, grouped by category",
    during: "During",
    outside: "Outside",
    footnote:
      "Positive values mean higher pain during events in this category. Based on available data in the selected period.",
    categories: {
      Medication: "Medication",
      "Medical Intervention": "Medical Intervention",
      "Health Status": "Health Status",
      "Lifestyle & Environment": "Lifestyle & Environment",
      Other: "Other",
    },
  },
  // --- Multi-Symptom Composite Trend ---
  composite: {
    title: "Symptom Trends by Channel",
    info: {
      what: "A multi-symptom view of pain, fatigue, cognitive fog, and sleep impact over time.",
      how: "Each channel uses its recorded daily 0-10 scores. The mini bars show recent daily values, and the badge shows the mean for available days.",
      why: "Symptoms often move together. Seeing channels side by side helps identify broader flare patterns beyond pain alone.",
    },
    description: "Separate symptom rows, each on a 0-10 scale",
    readingGuide: "Each row is a different symptom. Bars show recent daily entries; striped bars mean no entry for that day.",
    noData: "Not enough symptom data to display a composite view. Log fatigue, cognitive fog, or sleep impact to populate this chart.",
    pain: "Pain",
    fatigue: "Fatigue",
    cognitiveFog: "Cognitive fog",
    sleepImpact: "Sleep impact",
    legend: "Rows shown",
    avgLabel: "Avg",
    avgWithCount: "{{avgLabel}} {{value}}/10 (n={{count}})",
    noEntry: "No entry",
    windowLabel: "Sparkline window: last {{count}} days",
    windowRange: "{{start}} - {{end}}",
    daysWithData: "{{count}} days with data",
  },
  // --- Activity Pacing & Flare Detection ---
  pacing: {
    title: "Activity Pacing Insight",
    info: {
      what: "Whether higher activity days are followed by pain spikes (boom-bust pattern).",
      how: "The model counts activity levels and checks if moderate/heavy days are followed by elevated pain within 1-2 days, then compares average pain after high vs low activity days.",
      why: "This helps calibrate pacing. If boom-bust appears often, steadier activity pacing may reduce symptom rebounds.",
    },
    description: "Detects likely boom-bust patterns where high activity is followed by elevated pain",
    noData: "Not enough pacing data recorded. Log your daily activity level (rest / light / moderate / heavy) to see boom-bust pattern analysis.",
    boomBustDetected: "Boom-bust pattern detected",
    boomBustSummary: "{{count}} instance(s) where moderate/heavy activity days were followed by elevated pain within 1\u20132 days.",
    noBoomBust: "No clear boom-bust pattern detected in this period.",
    recentPattern: "Most recent: {{date}} ({{level}} activity \u2192 pain spike {{delta}})",
    avgPainAfterHeavy: "Avg pain after heavy/moderate days",
    avgPainAfterLight: "Avg pain after light/rest days",
    observation: "Observation",
    levelRest: "Rest",
    levelLight: "Light",
    levelModerate: "Moderate",
    levelHeavy: "Heavy",
    activityDays: "{{count}} days",
  },
  // --- Doctor Visit Summary ---
  doctorSummary: {
    title: "Doctor Visit Summary",
    description: "Concise overview for your healthcare appointment",
    periodBurden: "Period Burden",
    periodLabel: "{{start}} \u2013 {{end}}",
    avgPain: "Avg pain",
    peakPain: "Peak pain",
    trackedDays: "Tracked days",
    topRegions: "Top Pain Regions",
    flareRisk: "Flare Risk Snapshot",
    currentRisk: "Current projected risk",
    volatility: "Volatility",
    trendDirection: "Trend direction",
    keyEvents: "Key Event Impacts",
    noEvents: "No events recorded in this period.",
    pacingObs: "Pacing Observations",
    noPacingData: "No activity pacing data available for this period.",
    copySummary: "Copy Summary",
    copied: "Copied!",
    summaryHeader: "Pain Tracker \u2013 Visit Summary",
    boomBustNote: "Boom-bust pattern: {{count}} instance(s) detected",
    noBoomBustNote: "No boom-bust patterns detected",
  },
  // --- Filters ---
  filters: {
    title: "Filters",
    regionLabel: "Body Region",
    regionPlaceholder: "All regions",
    surfaceLabel: "Surface",
    surfacePlaceholder: "All surfaces",
    tagLabel: "Tags",
    tagPlaceholder: "All tags",
    locationGrouping: "Location grouping",
    groupZone: "Zone",
    groupZoneSide: "Zone + Side",
    groupZoneSideSurface: "Zone + Side + Surface",
    groupingHintZone: "Combines side and surface into one row per zone.",
    groupingHintZoneSide: "Keeps left/right split, combines front/back surfaces.",
    groupingHintZoneSideSurface: "Most detailed view: zone, side, and surface stay separate.",
    groupingMergedSuffix: "{{count}} row(s) merged by this grouping.",
    groupingMergedSuffixNone: "No rows merged in the current filtered set.",
    activeCount: "{{count}} active",
    clearAll: "Clear filters",
    noTags: "No tags recorded in this period",
    applied: "Filtered view",
    appliedHint: "Showing data matching {{count}} filter(s). Some analytics may show reduced data.",
    expandAriaLabel: "Expand filters",
    collapseAriaLabel: "Collapse filters",
    summaryAllRegions: "All regions",
    summaryAllSurfaces: "All surfaces",
    summaryAllTags: "All tags",
  },
  // --- Episode Storyline ---
  storyline: {
    title: "Episode Storyline",
    description: "Chronological narrative of events, check-ins, and notable pain changes",
    noData: "Not enough data to build a storyline. Continue logging check-ins and events.",
    entryCheckin: "Check-in",
    entryEvent: "Event",
    entryInflection: "Notable change",
    intensityLabel: "Intensity",
    intensityRise: "Pain rose to {{value}}",
    intensityDrop: "Pain dropped to {{value}}",
    eventStarted: "{{type}} started",
    eventEnded: "{{type}} ended",
    checkinNote: "{{count}} location(s), avg {{avg}}/10",
    showMore: "Show earlier entries",
    showLess: "Collapse",
  },
  // --- Doctor Export Blocks ---
  doctorExport: {
    title: "Export for Doctor",
    description: "Copy a formatted clinical summary to share with your healthcare provider",
    copyBlocks: "Copy Doctor Summary",
    copiedBlocks: "Copied to clipboard!",
    exportLabel: "Doctor-Ready Summary",
    sectionPeriod: "Period Overview",
    sectionRegions: "Top Pain Regions",
    sectionEvents: "Key Events",
    sectionRisk: "Risk & Stability",
    sectionPacing: "Activity Pacing",
    sectionComposite: "Multi-Symptom Overview",
    sectionConcerns: "Concerns for Visit",
    concernsLabel: "Concerns for this visit",
    concernsPlaceholder: "Add questions or concerns you want to discuss with your doctor...",
    generatedNote: "Generated from Floline pain tracker on {{date}}",
  },
  doctorVisitPrep: {
    title: "Doctor Visit Prep",
    description: "Review your summary, add concerns, and share with your healthcare provider",
    summaryPreview: "Summary Preview",
    shareSummary: "Share Summary",
  },
  // --- Temperature Correlation ---
  temperatureCorrelation: {
    title: "Temperature & Pain",
    info: {
      what: "How your average pain differs across temperature ranges.",
      how: "It pairs each day with both a pain value and a daily mean temperature, groups days into temperature buckets, and computes average pain per bucket.",
      why: "If specific temperature ranges repeatedly align with higher pain, you can plan pacing, routines, and event timing more proactively.",
    },
    noOverlap: "No overlapping temperature and pain data found for this period.",
    insufficientData:
      "Not enough data to analyse. Need at least {{totalNeeded}} days total ({{perGroupNeeded}} per group); currently have {{totalHave}} (cold {{coldHave}}, warm {{warmHave}}).",
    summary:
      "On days ≤ {{coldMax}} {{unit}}, average pain was {{coldAvg}}. On warmer days it was {{warmAvg}}.",
    days: "{{count}} days",
    disclaimer:
      "This is an observational summary, not a clinical finding. Many factors influence pain beyond temperature.",
    buckets: {
      leq0: "≤ 0 {{unit}}",
      "0to5": "0–5 {{unit}}",
      "5to10": "5–10 {{unit}}",
      "10to15": "10–15 {{unit}}",
      "15to20": "15–20 {{unit}}",
      gt20: "> 20 {{unit}}",
    },
  },
} as const;

export const trendsFi = {
  title: "Trendit",
  subtitle: "Kipumallit, tapahtumat ja analyyttiset oivallukset",
  export: "Vie",
  exportMovedCta: "Lääkärikäynnit",
  logEvent: "Kirjaa tapahtuma",
  chart: {
    title: "Kivun voimakkuuden trendi",
    info: {
      what: "Päivittäinen kivun keskivoimakkuus ajan yli, mukana valinnaiset tapahtuma- ja lämpötilatiedot.",
      how: "Jokainen piste on päivän keskimääräinen kipupisteesi (0-10). Katkoviiva on valitun jakson keskiarvo. Tapahtumajaksot näyttävät päivät, joille kirjatut tapahtumat osuvat.",
      why: "Näet nopeasti trendin suunnan, pahenemisjaksot ja sen, osuvatko tapahtumat kivun muutoksiin.",
    },
    loading: "Ladataan kaaviodataa...",
    noData: "Ei kipudataa tälle ajanjaksolle.",
    noDataHint: "Aloita seuranta nähdäksesi trendit.",
    noDataTooltip: "Ei kirjattua tietoa",
    eventsInPeriod: "Tapahtumat tällä jaksolla: {{count}}",
    moreEvents: "+{{count}} lisää",
    showMoreEvents: "Näytä {{count}} lisää",
    showLessEvents: "Näytä vähemmän",
    averagePain: "Keskikipu",
    meanLine: "Jakson keskiarvo",
    temperatureToggle: "Lämpötila",
    temperatureLabel: "Lämpötila",
    temperatureSetLocation: "Aseta sijainti asetuksissa",
    trendDateRange: "Trendin aikaväli",
    ariaLabel: "Kivun voimakkuuden trendikaavio ajalle {{range}}",
  },
  info: {
    aria: "Lisätietoa",
    whatLabel: "Mitä tämä näyttää",
    howLabel: "Miten tämä lasketaan",
    whyLabel: "Miksi tämä on tärkeää",
  },
  dateRange: {
    "7d": "7 päivää",
    "30d": "30 päivää",
    "90d": "90 päivää",
    "180d": "6 kuukautta",
    "365d": "1 vuosi",
    all: "Kaikki",
  },
  sections: {
    overview: "Yleiskuva",
    insights: "Oivallukset",
    events: "Tapahtumat",
    body: "Keho",
  },
  insights: {
    title: "Oivallukset",
    info: {
      what: "Valitun aikavälin datasta muodostetut AI-havainnot ja mallit.",
      how: "Oivallukset muodostetaan kirjauksistasi ja järjestetään tärkeysjärjestykseen. Ne sisältävät trendejä, malleja, korrelaatioita, edistymistä ja fokus-ehdotuksia.",
      why: "Ne auttavat havaitsemaan merkitykselliset muutokset nopeammin ja tukevat keskustelua hoitotiimin kanssa.",
    },
    show: "Näytä",
    hide: "Piilota",
    expandAriaLabel: "Laajenna oivallukset",
    collapseAriaLabel: "Pienennä oivallukset",
    noInsights: "Ei oivalluksia tälle ajanjaksolle.",
    noInsightsHint: "Jatka kirjaamista nähdäksesi malleja tiedoissasi.",
    error: "Oivallusten lataus epäonnistui. Yritä myöhemmin uudelleen.",
    type: {
      trend_observation: "Trendi",
      pattern_identification: "Malli",
      correlation_observation: "Havainto",
      progress_indicator: "Edistyminen",
      focus_suggestion: "Ehdotus",
    },
  },
  error: {
    title: "Trendien lataus epäonnistui",
    description: "Jokin meni pieleen kiputrendien latauksen aikana.",
    retry: "Yritä uudelleen",
  },
  empty: {
    title: "Ei trenditietoja vielä",
    description:
      "Aloita päivittäisen kivun seuranta nähdäksesi malleja ja trendejä ajan myötä. Tietosi auttavat tunnistamaan laukaisijat ja seuraamaan edistymistä.",
    startTracking: "Aloita seuranta",
  },
  stats: {
    sectionTitle: "Keskeiset mittarit",
    info: {
      what: "Valitun jakson keskeinen yhteenveto kirjausaktiivisuudesta ja kivun tasosta.",
      how: "Seuratut päivät laskee päivät, joilla on kirjauksia. Johdonmukaisuus/seuranta on seuratut päivät jaettuna jakson päivillä. Keskikipu on päivittäisten kipuarvojen keskiarvo, huippukipu korkein arvo.",
      why: "Mittarit kertovat datan kattavuuden ja oirekuorman perustason, jotta muun analytiikan tulkinta on luotettavampaa.",
    },
    daysTracked: "Seuratut päivät",
    consistency: "{{value}} % johdonmukaisuus",
    averagePain: "Keskikivut",
    dailyAverage: "Päivittäinen keskivoimakkuus",
    peakPain: "Huippukipu",
    highestRecorded: "Korkein kirjattu",
    tracking: "Seuranta",
    daysWithRecords: "Päivät, joissa kirjauksia",
  },
  locations: {
    title: "Kipukohteet",
    topTitle: "Yleisimmät kipukohteet",
    noLocations: "Ei kipukohteita tälle ajanjaksolle.",
    daysAvg: "{{days}} päivää \u2022 ka. {{avg}}/10",
    mostFrequent:
      "Yleisin sijainti ({{percentage}} % seuratuista päivistä)",
    moreLocations: "+{{count}} muuta seurattua sijaintia",
  },
  burden: {
    title: "Kehoalueen kuormitus",
    info: {
      what: "Alueittain järjestetty kuormituspisteytys kehon kipukuormasta.",
      how: "Jokaisen alueen piste lasketaan yhdistämällä tiheys (35 %), keskivoimakkuus (45 %) ja seurannan kattavuus (20 %) asteikolle 0-100, jonka jälkeen alueet lajitellaan suurimmasta pienimpään.",
      why: "Auttaa priorisoimaan alueet, joissa kipu on pysyvintä ja kuormittavinta, jotta toimenpiteet kohdistuvat olennaiseen.",
    },
    description:
      "Yhdistelmäpistemäärä, joka yhdistää kivun tiheyden, voimakkuuden ja seurannan kattavuuden. Korkeampi kuormitus = pysyvämpi, voimakkaampi kipu kyseisellä alueella.",
    tier: {
      critical: "Kriittinen",
      high: "Korkea",
      moderate: "Kohtalainen",
      low: "Matala",
    },
    segFrequency: "Tiheys: {{value}}",
    segIntensity: "Voimakkuus: {{value}}",
    segCoverage: "Kattavuus: {{value}}",
    legendFrequency: "Tiheys (35 %)",
    legendIntensity: "Voimakkuus (45 %)",
    legendCoverage: "Kattavuus (20 %)",
    footnote:
      "Kuormituspistemäärä (0\u2013100) = tiheyspaino (35 %) + voimakkuuspaino (45 %) + kattavuuspaino (20 %). Tämä on havainnoiva mittari, ei kliininen diagnoosi.",
  },
  // --- Vakaus & Volatiliteetti ---
  stability: {
    title: "Vakaus ja volatiliteetti",
    info: {
      what: "Kuinka tasaisena tai vaihtelevana kipusi pysyy tällä jaksolla.",
      how: "Volatiliteetti perustuu päivittäisen kivun keskihajontaan. Päivämuutos on päivittäisten muutosten keskimääräinen itseisarvo. Suunta vertaa jakson alkua ja loppua. Paras jakso laskee peräkkäiset päivät ilman nousua.",
      why: "Tasaisempi kuvio on helpompi ennakoida ja hallita. Nouseva vaihtelu tai suunta voi viitata tarpeeseen tarkistaa laukaisijoita, rytmitystä tai hoitoa.",
    },
    volatility: "Volatiliteetti",
    low: "Matala",
    moderate: "Kohtalainen",
    high: "Korkea",
    stdDev: "KH",
    dailySwing: "Päivämuutos",
    avgChange: "keskim. muutos",
    direction: "Suunta",
    trending_up: "Nouseva",
    trending_down: "Laskeva",
    stable: "Vakaa",
    ptsShift: "pisteen muutos",
    stableRun: "Paras jakso",
    days: "päivää",
    noIncrease: "ilman nousua",
    pts: "pist.",
  },
  // --- Pahenemiseneste ---
  forecast: {
    title: "Pahenemisennuste",
    info: {
      what: "Seitsemän päivän suhteellinen arvio kivun pahenemisriskistä.",
      how: "Ennustepisteet muodostetaan viimeaikaisista kipumalleista, viikonpäiväkäyttäytymisestä, tapahtumien päällekkäisyydestä ja haluttaessa sääsäädöstä.",
      why: "Tukee lyhyen aikavälin suunnittelua nostamalla esiin päivät, jolloin voi olla hyödyllistä keventää kuormitusta tai tehostaa oirehallintaa.",
    },
    description: "7 päivän riskiennuste viimeaikaisten malliesi perusteella",
    basedOn: "Perustuu viimeiseen {{days}} päivään",
    confidenceHigh: "Korkea luottamus",
    confidenceModerate: "Kohtalainen luottamus",
    confidenceLow: "Matala luottamus",
    dataPoints: "datapistettä",
    today: "Tänään",
    risk: {
      low: "Matala",
      moderate: "Kohtalainen",
      high: "Korkea",
    },
    footnote:
      "Tämä ennuste perustuu historiallisiin kipumalleihin, tapahtumapäällekkäisyyksiin ja viikonpäivätrendeihin. Se on havainnoiva, ei lääketieteellinen diagnoosi tai ennuste. Ota yhteyttä terveydenhuollon tarjoajaasi kliinisten neuvojen saamiseksi.",
    weatherAdjusted: "Säämallit huomioitu",
    weatherToggle: "LämpötilaS",
    weatherInfo:
      "Ennustepisteitä on mukautettu havaitun kipu-lämpötilasuhteesi ja tulevan sääennusteen perusteella. Tämä on havainnoiva, ei kliininen ennuste.",
    days: {
      sun: "su",
      mon: "ma",
      tue: "ti",
      wed: "ke",
      thu: "to",
      fri: "pe",
      sat: "la",
    },
  },
  // --- Tapahtuman vaikutus ---
  eventImpact: {
    title: "Tapahtuman vaikutus",
    info: {
      what: "Miten kipu muuttuu ennen tapahtumaa, tapahtuman aikana ja sen jälkeen.",
      how: "Jokaiselle tapahtumalle lasketaan keskikipu kolmessa ikkunassa: 7 päivää ennen, tapahtuman aikana ja 7 päivää jälkeen. Muutos = aikana miinus ennen.",
      why: "Auttaa tunnistamaan tapahtumat, joihin liittyy mahdollinen paraneminen tai paheneminen käytännössä.",
    },
    description:
      "Keskikipu 7 päivää ennen, aikana ja jokaisen tapahtuman jälkeen",
    event: "Tapahtuma",
    before: "Ennen",
    during: "Aikana",
    after: "Jälkeen",
    change: "Muutos",
    sortBy: "Järjestä",
    sortDirection: "Järjestys",
    sort: {
      default: "Oletus",
      name: "Tapahtuman nimi",
      before: "Ennen",
      during: "Aikana",
      after: "Jälkeen",
      change: "Muutos",
      asc: "Nouseva",
      desc: "Laskeva",
    },
  },
  // --- Kategoriavaikutus ---
  categoryImpact: {
    title: "Kategoriavaikutus",
    info: {
      what: "Kategoriatason vertailu kivusta tapahtumien aikana ja niiden ulkopuolella.",
      how: "Tapahtumat ryhmitellään kategorioittain, jonka jälkeen lasketaan keskikipu tapahtumapäiville ja niiden ulkopuolisille päiville. Delta = aikana miinus ulkopuolella.",
      why: "Paljastaa laajempia kokonaisuuksia (esim. lääkitys tai terveydentila), joita yksittäiset tapahtumat eivät aina näytä.",
    },
    description:
      "Keskikipu tapahtumien aikana vs. niiden ulkopuolella, ryhmiteltynä kategorioittain",
    during: "Aikana",
    outside: "Ulkopuol.",
    footnote:
      "Positiiviset arvot tarkoittavat korkeampaa kipua tämän kategorian tapahtumien aikana. Perustuu valitun ajanjakson saatavilla olevaan dataan.",
    categories: {
      Medication: "Lääkitys",
      "Medical Intervention": "Lääketieteellinen toimenpide",
      "Health Status": "Terveydentila",
      "Lifestyle & Environment": "Elämäntapa ja ympäristö",
      Other: "Muu",
    },
  },
  // --- Monioireinen yhdistelmänäkymä ---
  composite: {
    title: "Oiretrendit kanavittain",
    info: {
      what: "Monioireinen näkymä kivusta, väsymyksestä, kognitiivisesta sumusta ja univaikutuksesta ajan yli.",
      how: "Jokainen kanava käyttää kirjattuja päivittäisiä 0-10 arvoja. Minipalkit näyttävät viimeisimmät päivät, ja tunnusluku näyttää kanavan keskiarvon.",
      why: "Oireet muuttuvat usein yhdessä. Rinnakkainen näkymä auttaa tunnistamaan laajempia pahenemisjaksoja pelkän kivun lisäksi.",
    },
    description: "Erilliset oirekanavat, jokainen asteikolla 0-10",
    readingGuide: "Jokainen rivi on eri oire. Palkit näyttävät viimeisimmät päiväkirjaukset; viivoitetut palkit tarkoittavat, ettei kirjausta ole.",
    noData: "Ei tarpeeksi oiredataa yhdistelmänäkymään. Kirjaa väsymys, kognitiivinen sumu tai univaikutus täyttääksesi tämän kaavion.",
    pain: "Kipu",
    fatigue: "Väsymys",
    cognitiveFog: "Kogn. sumu",
    sleepImpact: "Univaikutus",
    legend: "Näytetyt rivit",
    avgLabel: "Ka.",
    avgWithCount: "{{avgLabel}} {{value}}/10 (n={{count}})",
    noEntry: "Ei kirjausta",
    windowLabel: "Pikkukaavion ikkuna: viimeiset {{count}} päivää",
    windowRange: "{{start}} - {{end}}",
    daysWithData: "{{count}} päivää kirjattu",
  },
  // --- Aktiivisuusrytmitys & pahenemistunnistus ---
  pacing: {
    title: "Aktiivisuusrytmityksen oivallus",
    info: {
      what: "Seuraa, johtaako korkeampi aktiivisuus usein kipupiikkiin (ylirasitus-romahduskuvio).",
      how: "Malli laskee aktiivisuustasot ja tarkistaa, seuraako kohtalaista/raskasta päivää kohonnut kipu 1-2 päivän sisällä. Lisäksi se vertaa kipukeskiarvoja korkean ja matalan aktiivisuuden jälkeen.",
      why: "Auttaa säätämään arjen rytmitystä. Jos kuvio toistuu, tasaisempi kuormitus voi vähentää oireiden heilahtelua.",
    },
    description: "Tunnistaa todennäköiset ylirasitus-romahduskuviot, joissa korkea aktiivisuus seuraa kohonnutta kipua",
    noData: "Ei tarpeeksi rytmitysdataa. Kirjaa päivittäinen aktiivisuustaso (lepo / kevyt / kohtalainen / raskas) nähdäksesi ylirasituskuvioanalyysin.",
    boomBustDetected: "Ylirasitus-romahduskuvio havaittu",
    boomBustSummary: "{{count}} tapausta, joissa kohtalainen/raskas aktiivisuuspäivä johti kohonneeseen kipuun 1\u20132 päivän kuluessa.",
    noBoomBust: "Ei selkeää ylirasitus-romahduskuviota tällä jaksolla.",
    recentPattern: "Viimeisin: {{date}} ({{level}} aktiivisuus \u2192 kipupiikki {{delta}})",
    avgPainAfterHeavy: "Keskim. kipu raskaan/kohtalaisen jälkeen",
    avgPainAfterLight: "Keskim. kipu kevyen/levon jälkeen",
    observation: "Havainto",
    levelRest: "Lepo",
    levelLight: "Kevyt",
    levelModerate: "Kohtalainen",
    levelHeavy: "Raskas",
    activityDays: "{{count}} päivää",
  },
  // --- Lääkärikäynnin yhteenveto ---
  doctorSummary: {
    title: "Lääkärikäynnin yhteenveto",
    description: "Tiivis katsaus terveydenhuollon tapaamiseen",
    periodBurden: "Jakson kuormitus",
    periodLabel: "{{start}} \u2013 {{end}}",
    avgPain: "Keskim. kipu",
    peakPain: "Huippukipu",
    trackedDays: "Seuratut päivät",
    topRegions: "Yleisimmät kipualueet",
    flareRisk: "Pahenemisriskin tilannekuva",
    currentRisk: "Nykyinen ennustettu riski",
    volatility: "Volatiliteetti",
    trendDirection: "Trendin suunta",
    keyEvents: "Tapahtumien vaikutukset",
    noEvents: "Ei kirjattuja tapahtumia tällä jaksolla.",
    pacingObs: "Rytmityshavainnot",
    noPacingData: "Ei aktiivisuusrytmitysdataa tälle ajanjaksolle.",
    copySummary: "Kopioi yhteenveto",
    copied: "Kopioitu!",
    summaryHeader: "Kipuseuranta \u2013 Käyntiyhteenveto",
    boomBustNote: "Ylirasituskuvio: {{count}} tapausta havaittu",
    noBoomBustNote: "Ei ylirasituskuvioita havaittu",
  },
  // --- Suodattimet ---
  filters: {
    title: "Suodattimet",
    regionLabel: "Kehon alue",
    regionPlaceholder: "Kaikki alueet",
    surfaceLabel: "Pinta",
    surfacePlaceholder: "Kaikki pinnat",
    tagLabel: "Tunnisteet",
    tagPlaceholder: "Kaikki tunnisteet",
    locationGrouping: "Sijainnin ryhmittely",
    groupZone: "Alue",
    groupZoneSide: "Alue + puoli",
    groupZoneSideSurface: "Alue + puoli + pinta",
    groupingHintZone: "Yhdistää puolen ja pinnan yhdeksi riviksi per alue.",
    groupingHintZoneSide: "Säilyttää vasen/oikea-erottelun, yhdistää etu/taka-pinnat.",
    groupingHintZoneSideSurface: "Tarkin näkymä: alue, puoli ja pinta pidetään erillään.",
    groupingMergedSuffix: "{{count}} rivi(ä) yhdistetty tällä ryhmittelyllä.",
    groupingMergedSuffixNone: "Nykyisessä suodatuksessa rivejä ei yhdistetty.",
    activeCount: "{{count}} aktiivinen",
    clearAll: "Tyhjennä suodattimet",
    noTags: "Ei tunnisteita tällä jaksolla",
    applied: "Suodatettu näkymä",
    appliedHint: "Näytetään {{count}} suodattimen mukaiset tiedot. Osa analytiikasta voi näyttää vähemmän dataa.",
    expandAriaLabel: "Laajenna suodattimet",
    collapseAriaLabel: "Supista suodattimet",
    summaryAllRegions: "Kaikki alueet",
    summaryAllSurfaces: "Kaikki pinnat",
    summaryAllTags: "Kaikki tunnisteet",
  },
  // --- Tapahtumaketju ---
  storyline: {
    title: "Tapahtumaketju",
    description: "Kronologinen kertomus tapahtumista, kirjauksista ja merkittävistä kipumuutoksista",
    noData: "Ei tarpeeksi dataa tapahtumaketjun rakentamiseen. Jatka kirjausten ja tapahtumien tallentamista.",
    entryCheckin: "Kirjaus",
    entryEvent: "Tapahtuma",
    entryInflection: "Merkittävä muutos",
    intensityLabel: "Intensiteetti",
    intensityRise: "Kipu nousi tasolle {{value}}",
    intensityDrop: "Kipu laski tasolle {{value}}",
    eventStarted: "{{type}} alkoi",
    eventEnded: "{{type}} päättyi",
    checkinNote: "{{count}} sijaintia, keskim. {{avg}}/10",
    showMore: "Näytä aiemmat merkinnät",
    showLess: "Supista",
  },
  // --- Lääkärille vienti ---
  doctorExport: {
    title: "Vie lääkärille",
    description: "Kopioi muotoiltu kliininen yhteenveto jaettavaksi terveydenhuollon tarjoajalle",
    copyBlocks: "Kopioi lääkäriyhteenveto",
    copiedBlocks: "Kopioitu leikepöydälle!",
    exportLabel: "Lääkärivalmis yhteenveto",
    sectionPeriod: "Jakson yleiskatsaus",
    sectionRegions: "Yleisimmät kipualueet",
    sectionEvents: "Keskeiset tapahtumat",
    sectionRisk: "Riski ja vakaus",
    sectionPacing: "Aktiivisuuden rytmitys",
    sectionComposite: "Monioireinen yleiskatsaus",
    sectionConcerns: "Käynnin huolenaiheet",
    concernsLabel: "Huolenaiheet tähän käyntiin",
    concernsPlaceholder: "Lisää kysymykset tai huolenaiheet, joista haluat keskustella lääkärin kanssa...",
    generatedNote: "Luotu Floline-kipuseurannasta {{date}}",
  },
  doctorVisitPrep: {
    title: "Lääkärikäynnin valmistelu",
    description: "Tarkista yhteenveto, lisää huolenaiheet ja jaa terveydenhuollon tarjoajalle",
    summaryPreview: "Yhteenvedon esikatselu",
    shareSummary: "Jaa yhteenveto",
  },
  // --- Lämpötilakorrelaatio ---
  temperatureCorrelation: {
    title: "Lämpötila ja kipu",
    info: {
      what: "Miten keskikipu vaihtelee eri lämpötila-alueilla.",
      how: "Jokaiselle päivälle yhdistetään kipuarvo ja vuorokauden keskilämpötila, päivät ryhmitellään lämpötilaluokkiin ja kullekin luokalle lasketaan keskikipu.",
      why: "Jos tietyt lämpötila-alueet toistuvasti liittyvät korkeampaan kipuun, voit suunnitella rytmitystä, rutiineja ja tapahtumien ajoitusta ennakoivammin.",
    },
    noOverlap: "Ei päällekkäistä lämpötila- ja kipudataa tälle ajanjaksolle.",
    insufficientData:
      "Ei tarpeeksi dataa analyysiin. Tarvitaan vähintään {{totalNeeded}} päivää yhteensä ({{perGroupNeeded}} per ryhmä); nyt käytössä {{totalHave}} (kylmä {{coldHave}}, lämmin {{warmHave}}).",
    summary:
      "Päivinä ≤ {{coldMax}} {{unit}} keskikipu oli {{coldAvg}}. Lämpimämpinä päivinä se oli {{warmAvg}}.",
    days: "{{count}} päivää",
    disclaimer:
      "Tämä on havainnoiva yhteenveto, ei kliininen löydös. Monet tekijät vaikuttavat kipuun lämpötilan lisäksi.",
    buckets: {
      leq0: "≤ 0 {{unit}}",
      "0to5": "0–5 {{unit}}",
      "5to10": "5–10 {{unit}}",
      "10to15": "10–15 {{unit}}",
      "15to20": "15–20 {{unit}}",
      gt20: "> 20 {{unit}}",
    },
  },
} as const;
